import React, { createContext, useContext, useMemo } from 'react';
import {
  FeedbackGroup,
  FeedbackGroupState,
} from '../../types/feedback/FeedbackGroup';
import { ItemGroup } from '../../types/orderDetails';
import { FeedbackType } from '../../types/feedback/Feedback';

/**
 * A context for storing order-level customer feedback group data.
 */
interface ICustomerFeedbackGroupsContext {
  feedbackGroups: FeedbackGroup[];
}

const CustomerFeedbackGroupsContext =
  createContext<ICustomerFeedbackGroupsContext>({
    feedbackGroups: [],
  });

/**
 * A provider to make order-level customer feedback group data available to all children components.
 */
type CustomerFeedbackGroupsProviderProps = {
  children: React.ReactNode;
  feedbackGroups: FeedbackGroup[];
};

const CustomerFeedbackGroupsProvider = ({
  children,
  feedbackGroups,
}: CustomerFeedbackGroupsProviderProps) => {
  const feedbackGroupsDataContextValues = useMemo(
    () => ({
      feedbackGroups,
    }),
    [feedbackGroups]
  );

  return (
    <CustomerFeedbackGroupsContext.Provider
      value={feedbackGroupsDataContextValues}
    >
      {children}
    </CustomerFeedbackGroupsContext.Provider>
  );
};

/**
 * This is a helper function to get feedback group result for the given line item.
 *
 * @returns A feedback group result associated with the given line item.
 */
const getFeedbackGroupResult = (
  feedbackGroups: FeedbackGroup[],
  lineItemId: string,
  requestType: FeedbackType,
  groupState: FeedbackGroupState
) => {
  /**
   * Returns the customer feedback group that meets all of the criteria.
   * If none are found, returns an empty object.
   */
  const customerFeedbackGroup =
    feedbackGroups.filter(
      feedbackGroup =>
        feedbackGroup.lineItemIds.includes(lineItemId) &&
        feedbackGroup.type === requestType &&
        feedbackGroup.state === groupState
    )[0] || {};

  /**
   * Returns `true` if a customer feedback group exists that meets all of the criteria.
   */
  const customerFeedbackGroupExists =
    Object.keys(customerFeedbackGroup).length > 0;

  return {
    customerFeedbackGroup,
    customerFeedbackGroupExists,
  };
};

/**
 *
 * @returns A feedback group result associated with the given line item.
 */
type UseCustomerFeedbackGroupParams = {
  lineItemId: string;
  requestType: FeedbackType;
  groupState: FeedbackGroupState;
};

const useCustomerFeedbackGroup = ({
  lineItemId,
  requestType,
  groupState,
}: UseCustomerFeedbackGroupParams) => {
  const { feedbackGroups = [] } = useContext(CustomerFeedbackGroupsContext);

  return getFeedbackGroupResult(
    feedbackGroups,
    lineItemId,
    requestType,
    groupState
  );
};

/**
 *
 * @returns A feedback group result associated with any given line items in the itemGroup.
 */
type UseCustomerFeedbackGroupForItemGroupParams = {
  itemGroup: ItemGroup;
  requestType: FeedbackType;
  groupState: FeedbackGroupState;
};

const useCustomerFeedbackGroupForItemGroup = ({
  itemGroup,
  requestType,
  groupState,
}: UseCustomerFeedbackGroupForItemGroupParams) => {
  const { lineItemIds } = itemGroup;

  const { feedbackGroups = [] } = useContext(CustomerFeedbackGroupsContext);

  for (let i = 0; i < lineItemIds.length; i += 1) {
    const result = getFeedbackGroupResult(
      feedbackGroups,
      lineItemIds[i],
      requestType,
      groupState
    );
    if (result.customerFeedbackGroupExists) return result;
  }

  return {
    customerFeedbackGroup: {},
    customerFeedbackGroupExists: false,
  };
};

export {
  CustomerFeedbackGroupsProvider,
  CustomerFeedbackGroupsContext,
  useCustomerFeedbackGroup,
  useCustomerFeedbackGroupForItemGroup,
};
